import React from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';
import Button from '../../Button';
import '../../shared/Card.css';
import './ContentBannerV2.css';

/**
 * ContentBannerV2 — dark two-column banner: a text column (eyebrow, editorial
 * title, paragraph, a check-list of `features` and/or a numbered list of
 * `steps`, CTA) next to a visual column.
 *
 * The visual is driven by the data:
 * - 2+ `images` → a 2×2 mosaic of dark cards with staggered tops (Figma
 *   "Content Banner V2"); on mobile the cards become a horizontal scroller.
 * - 1 image (or a `video`) → one large preview card, optionally decorated
 *   with `stickers` hanging over its top corners (Figma "… – Reversed").
 *
 * `visualPosition="end"` (the old `reversed`) swaps the columns on desktop (text left, visual right). Mobile
 * always stacks text first.
 *
 * Figma (OVER-LL): `2217:151813` / mobile `2645:13393` (default),
 * `2275:15287` / mobile `2645:13488` (reversed).
 */
const ContentBannerV2 = ({
  visualPosition = 'start',
  reversed = false,
  visual = 'auto',
  subtitle = '',
  title = '',
  paragraph = '',
  paragraphVariant = 'large',
  features = [],
  steps = [],
  ctaLabel = '',
  ctaVariant = 'gray',
  ctaIcon,
  ctaHref,
  ctaOnClick,
  images = [],
  video,
  stickers = [],
  className = '',
  ...rest
}) => {
  // 'auto' picks by data (2+ images → mosaic); 'mosaic' / 'preview' force it
  const isMosaic = visual === 'mosaic' || (visual === 'auto' && images.length >= 2);
  const single = images[0];
  const hasVisual = isMosaic || Boolean(single) || Boolean(video);
  if (isMosaic && images.length !== 4 && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`ContentBannerV2: the mosaic is styled for exactly 4 images (Figma 2217:151813), got ${images.length}.`);
  }

  // Visual at the start (left, default — Figma 2217:151813) or the end (right —
  // 2275:15287). `reversed` is the old spelling of visualPosition="end".
  const visualEnd = visualPosition === 'end' || reversed;

  const rootClass = [
    'content-banner-v2',
    visualEnd && 'content-banner-v2--reversed',
    className,
  ].filter(Boolean).join(' ');

  const paragraphClass = [
    'content-banner-v2__paragraph',
    `content-banner-v2__paragraph--${paragraphVariant}`,
    paragraphVariant === 'editorial' ? 'typo-body-editorial' : 'typo-body-lg',
  ].join(' ');

  return (
    <section className={rootClass} {...rest}>
      <div className="content-banner-v2__container">
        <div className="content-banner-v2__text">
          {subtitle && <p className="content-banner-v2__subtitle typo-body">{subtitle}</p>}
          {title && <h2 className="content-banner-v2__title typo-h1-editorial">{title}</h2>}
          {paragraph && <p className={paragraphClass}>{paragraph}</p>}

          {features.length > 0 && (
            <ul className="content-banner-v2__features">
              {features.map(({ title: featureTitle, description }) => (
                <li key={featureTitle} className="content-banner-v2__feature">
                  <Check className="content-banner-v2__check" size={16} aria-hidden="true" />
                  <div className="content-banner-v2__feature-body">
                    <p className="content-banner-v2__feature-title typo-h5">{featureTitle}</p>
                    {description && (
                      <p className="content-banner-v2__feature-desc typo-body">{description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {steps.length > 0 && (
            <ol className="content-banner-v2__steps">
              {steps.map((step, index) => (
                <li key={step} className="content-banner-v2__step">
                  <span className="content-banner-v2__step-num typo-body-sm" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span className="content-banner-v2__step-text typo-body-lg">{step}</span>
                </li>
              ))}
            </ol>
          )}

          {ctaLabel && (
            <div className="content-banner-v2__cta">
              <Button size="secondary" variant={ctaVariant} iconRight={ctaIcon} href={ctaHref} onClick={ctaOnClick}>
                {ctaLabel}
              </Button>
            </div>
          )}
        </div>

        {hasVisual && (
          <div className={`content-banner-v2__visuals${isMosaic ? '' : ' content-banner-v2__visuals--preview'}`}>
            {isMosaic ? (
              <div className="content-banner-v2__mosaic">
                {images.map(({ src, alt }, index) => (
                  <figure key={src || index} className="ds-card content-banner-v2__card">
                    <img className="content-banner-v2__img" src={src} alt={alt || ''} loading="lazy" />
                  </figure>
                ))}
              </div>
            ) : (
              <div className="content-banner-v2__preview">
                <div className="ds-card content-banner-v2__card content-banner-v2__card--preview">
                  {video ? (
                    <video
                      className="content-banner-v2__img content-banner-v2__img--preview"
                      src={video}
                      poster={single?.src}
                      muted
                      autoPlay
                      loop
                      playsInline
                      aria-label={single?.alt || undefined}
                    />
                  ) : (
                    <picture className="content-banner-v2__picture">
                      {single.srcMobile && (
                        <source media="(max-width: 1023px)" srcSet={single.srcMobile} />
                      )}
                      <img
                        className="content-banner-v2__img content-banner-v2__img--preview"
                        src={single.src}
                        alt={single.alt || ''}
                        loading="lazy"
                      />
                    </picture>
                  )}
                </div>
                {stickers.map(({ src, alt, position = 'top-left' }) => (
                  <img
                    key={src}
                    className={`content-banner-v2__sticker content-banner-v2__sticker--${position}`}
                    src={src}
                    alt={alt || ''}
                    aria-hidden={alt ? undefined : 'true'}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

ContentBannerV2.propTypes = {
  /** Text left, visual right on desktop (Figma "Content Banner V2 – Reversed") */
  /** Side of the visual on desktop: 'start' (left, default) or 'end' (right) */
  visualPosition: PropTypes.oneOf(['start', 'end']),
  /** @deprecated Use `visualPosition="end"`. */
  reversed: PropTypes.bool,
  /** 'auto' (2+ images → mosaic, else preview), or force 'mosaic' / 'preview' */
  visual: PropTypes.oneOf(['auto', 'mosaic', 'preview']),
  /** CTA link target — renders the CTA as a real link */
  ctaHref: PropTypes.string,
  /** Eyebrow above the title (24px rule + uppercase text) */
  subtitle: PropTypes.string,
  /** Title (typo-h1-editorial) */
  title: PropTypes.string,
  /** Paragraph under the title */
  paragraph: PropTypes.string,
  /** Paragraph style: 'large' (P.Large, gray-6) or 'editorial' (P.Editorial, gray-5) */
  paragraphVariant: PropTypes.oneOf(['large', 'editorial']),
  /** Check-list rows [{ title, description }] */
  features: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
    }),
  ),
  /** Numbered steps (strings) */
  steps: PropTypes.arrayOf(PropTypes.string),
  /** CTA label (Button secondary); omit it and no button renders */
  ctaLabel: PropTypes.string,
  /** CTA Button variant */
  ctaVariant: PropTypes.oneOf([
    'main', 'alternative', 'danger', 'outline', 'black', 'white', 'gray', 'text',
  ]),
  /** CTA icon (rendered as iconRight) */
  ctaIcon: PropTypes.node,
  /** CTA handler */
  ctaOnClick: PropTypes.func,
  /** Visual: 2+ entries → mosaic, 1 entry → preview card. `srcMobile` = portrait preview on phones */
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      srcMobile: PropTypes.string,
      alt: PropTypes.string,
    }),
  ),
  /** Preview-card video (muted autoplay loop); images[0] becomes its poster */
  video: PropTypes.string,
  /** Decorations hanging over the preview card's top corners */
  stickers: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
      position: PropTypes.oneOf(['top-left', 'top-right']),
    }),
  ),
  /** Additional CSS class */
  className: PropTypes.string,
};

export default ContentBannerV2;
